import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/agent`;

const extractError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((e) => `${e.loc?.slice(-1)[0]}: ${e.msg}`).join(", ");
  }
  if (Array.isArray(d)) {
    return d.map((e) => `${e.loc?.slice(-1)[0]}: ${e.msg}`).join(", ");
  }
  return typeof d === "object" ? JSON.stringify(d) : String(d);
};

export const createClient = async (intake) => {
  try {
    const { data } = await axios.post(`${API}/clients`, intake);
    return data;
  } catch (err) {
    throw new Error(extractError(err));
  }
};

export const startAudit = async (clientId) => {
  try {
    const { data } = await axios.post(`${API}/clients/${clientId}/audit`, {});
    return data;
  } catch (err) {
    throw new Error(extractError(err));
  }
};

const mkErr = (err) => {
  const e = new Error(extractError(err));
  if (err?.response?.status === 409) e.compliance = true;
  return e;
};

// Portfolios group a client's businesses (max 5). Created once, then every
// business — including the first — is added via new-project.
export const createPortfolio = async () => {
  try {
    const { data } = await axios.post(`${API}/portfolios`, {});
    return data; // { portfolioId, url }
  } catch (err) {
    throw new Error(extractError(err));
  }
};

export const createProject = async (portfolioId, intake) => {
  try {
    const { data } = await axios.post(
      `${API}/portfolios/${portfolioId}/new-project`,
      intake,
    );
    return data; // { clientId, jobId }
  } catch (err) {
    const e = new Error(extractError(err));
    // 409 here means the portfolio is full (5-business cap reached).
    if (err?.response?.status === 409) e.portfolioFull = true;
    if (err?.response?.status === 404) e.notFound = true;
    throw e;
  }
};

export const approvePlan = async (clientId, body = {}) => {
  try {
    const { data } = await axios.post(
      `${API}/clients/${clientId}/approve-plan`,
      body,
    );
    return data;
  } catch (err) {
    throw mkErr(err);
  }
};

export const expandCampaign = async (clientId, body = {}) => {
  try {
    const { data } = await axios.post(
      `${API}/clients/${clientId}/expand-campaign`,
      body,
    );
    return data;
  } catch (err) {
    throw mkErr(err);
  }
};

export const getJob = async (jobId) => {
  try {
    const { data } = await axios.get(`${API}/jobs/${jobId}`);
    return data;
  } catch (err) {
    throw new Error(extractError(err));
  }
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Poll a job until it reaches the target stage with status "done".
// onProgress receives each job snapshot so the UI can show live stages.
export const pollJob = async (jobId, targetStage, onProgress, maxAttempts = 120) => {
  for (let i = 0; i < maxAttempts; i++) {
    const job = await getJob(jobId);
    if (onProgress) onProgress(job);
    if (job.status === "error" || job.error) {
      throw new Error(job.error || "The run failed. Please try again.");
    }
    if (job.stage === targetStage && job.status === "done") {
      return job;
    }
    await sleep(3000);
  }
  throw new Error("Timed out waiting for the run to finish.");
};
